import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import classNames from 'classnames';
import React from 'react';

import {deleteAnalysisSteps, runAnalysisStep} from 'analysis-tool/AnalysisActions';
import {
  MULTIPLE_CHARTS_OUTPUT_STEP_DATA_NAME,
  SaveToResultsStepDataName,
  SendToDocumentStepDataName,
  SINGLE_CHART_OUTPUT_STEP_DATA_NAME,
  SINGLE_DATASET_OUTPUT_STEP_DATA_NAME,
  SINGLE_FILE_OUTPUT_STEP_DATA_NAME,
} from 'analysis-tool/AnalysisStepDataNames';
import {ANALYSIS_TOOL_SPACING} from 'analysis-tool/AnalysisToolConstants';
import {
  AnalysisGraphQL,
  GetAnalysisPipelineStep,
  GetAnalysisChart,
  isAnalysisGraphQL,
} from 'analysis-tool/AnalysisTypes';
import {
  findAndAssertSingleStepChart,
  findAndAssertStepDataCharts,
} from 'analysis-tool/data/findAndAssertStepDataCharts';
import {findAndAssertSingleStepDataFrameId} from 'analysis-tool/data/findAndAssertStepDataFrameIds';
import {getNullableStepFromAnalysis} from 'analysis-tool/data/getStepFromAnalysis';
import DeleteFailedSaveToResultsStepBanner from 'analysis-tool/structured-data-mapping/DeleteFailedSaveToResultsStepBanner';
import SavedResultsQuery from 'analysis-tool/structured-data-mapping/SavedResultsQuery';
import useLogAnalysisProfilerTimingFinished from 'analysis-tool/ui/hooks/useLogAnalysisProfilerTimingFinished';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import isArchived from 'archive/isArchived';
import {assertAndContinue} from 'Asserts';
import {QueryingChartContent} from 'charts/chart-content/ChartContent';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import PersistedDataFrameTableWrapper from 'data-frames/wrapper/PersistedDataFrameTableWrapper';
import {
  ActionOrigin,
  PipelineStepDataKind,
  PipelineStepStatusCode,
  PipelineStepType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import navigateToURL from 'lib/navigateToURL';
import {standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import {SEND_TO_DOCUMENT_FINISHED_EVENT} from 'performance/processAnalysesEvents';
import {readStepTypeInfosFromCache} from 'pipelines/hooks/useStepTypeInfosQuery';
import {DeletePipelineStepsBehavior} from 'pipelines/network/PipelineDAO';
import {PipelineEdge, pipelineStepRawDataIsKind} from 'pipelines/PipelineTypes';
import {getParentSteps} from 'pipelines/StepLineageUtils';
import {getPipelineStepConfig} from 'pipelines/StepShapeUtils';
import {getUserFacingStepTypeName} from 'pipelines/StepTypeInfoUtils';
import AssayResultsTable from 'runs/AssayResultsTable';
import {FONT_PROXIMA_11, FONT_PROXIMA_18} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import runConfirmDeleteModal from 'ui-library/modals/runConfirmDeleteModal';
import Pane from 'ui-library/pane/Pane';
import {withLoadingScreen} from 'util/Promises';

import DocumentName from './DocumentName';
import {getDocumentIdFromStep, getPlateEdgeLabel, getSendToDocumentStepConfig} from './OutputsUtil';
import ScratchPlateOutputPreview from './ScratchPlateOutputPreview';
import {ScratchPlateOutputNode} from './Types';
import useDocumentMetadata from './useDocumentMetadata';

export const SEND_TO_DOCUMENT_VIEW_RESULT_ROW_LIMIT = 50;

interface SendToDocumentHeaderProps {
  analysis: AnalysisGraphQL;
  analysisClientStateId: string;
  step: GetAnalysisPipelineStep;
}

export function SendToDocumentHeader({
  analysis,
  analysisClientStateId,
  step,
}: SendToDocumentHeaderProps): JSX.Element {
  const {documentType, editUrl} = useDocumentMetadata(getDocumentIdFromStep(step));

  // Refer to both `DOCUMENT` and `WORKSHEET` as 'worksheet', since documents are steps within worksheets
  const documentText = documentType === ModelTypeConstants.ENTRY ? 'entry' : 'worksheet';

  const documentName = (
    <div className={css(styles.documentName)}>
      <DocumentName step={step} />
    </div>
  );

  switch (step.statusCode) {
    case PipelineStepStatusCode.SUCCEEDED:
    case PipelineStepStatusCode.SUCCEEDED_WITH_ERRORS:
      return (
        <div {...getTestProps({component: 'SendToDocumentView', element: 'root'})}>
          <div className={css(styles.helpText)}>Sent to</div>
          <div className={css(styles.headerLineWrapper)}>
            <BenchlingIcon icon={documentText} className={css(styles.documentIcon)} />
            <Button
              icon="open"
              className={css(styles.openDocumentButton)}
              buttonStyle={ButtonStyle.LINK}
              onClick={() => {
                if (editUrl) {
                  navigateToURL(editUrl);
                }
              }}
              dataTestAttrs={{component: 'SendToDocumentView', element: 'openDocumentButton'}}
              disabled={editUrl == null}
            >
              <div className="u-mini-spacing-right">{documentName}</div>
            </Button>
          </div>
        </div>
      );
    case PipelineStepStatusCode.FAILED:
      return (
        <>
          <div className={classNames(css(styles.headerLineWrapper), 'u-mini-spacing-bottom')}>
            <BenchlingIcon icon={documentText} className={css(styles.documentIcon)} />
            {documentName}
          </div>
          <InlineBanner
            bannerType={InlineBannerType.ERROR}
            text={`Error sending to ${documentText}`}
            supportingInfo={step.statusMessage ?? ''}
          />
          <Button
            className="u-small-spacing-top"
            buttonStyle={ButtonStyle.PRIMARY}
            disabled={isArchived(analysis)}
            icon="rotate-right"
            onClick={() =>
              withLoadingScreen(runAnalysisStep(analysis.id, {stepId: step.id}), 'Retrying send data...')
            }
            size={ButtonSize.SMALL}
            text="Retry send data"
          />
          <Button
            className="u-small-spacing-top u-mini-spacing-left"
            buttonStyle={ButtonStyle.NEGATIVE}
            disabled={isArchived(analysis)}
            icon="delete"
            onClick={() => {
              runConfirmDeleteModal({typeName: 'failed sending data'}).then((confirmed) => {
                if (!confirmed) {
                  return;
                }
                withLoadingScreen(
                  deleteAnalysisSteps(
                    analysis,
                    {
                      stepIds: [step.id],
                      behavior: DeletePipelineStepsBehavior.NO_INFERENCE,
                    },
                    analysisClientStateId
                  ),
                  'Deleting send data...'
                );
              });
            }}
            size={ButtonSize.SMALL}
            text="Delete send data"
          />
        </>
      );
    case PipelineStepStatusCode.RUNNING:
    case PipelineStepStatusCode.AI_PROPOSED:
    case PipelineStepStatusCode.WAITING:
    case PipelineStepStatusCode.BLOCKED:
      return (
        <>
          <div className={css(styles.headerLineWrapper)}>
            <BenchlingIcon
              icon="hourglass-half"
              className={classNames(css(styles.hourglassIcon), 'u-mini-spacing-right')}
            />
            <div className={css(styles.helpText)}>Sending to...</div>
          </div>
          <div className={css(styles.headerLineWrapper)}>
            <BenchlingIcon icon={documentText} className={css(styles.documentIcon)} />
            {documentName}
          </div>
        </>
      );
    case PipelineStepStatusCode.HALTED:
      return (
        <div>
          <div className={css(styles.helpText)}>Halted</div>
          <div className={css(styles.headerLineWrapper)}>
            <BenchlingIcon icon={documentText} className={css(styles.documentIcon)} />
            {documentName}
          </div>
        </div>
      );
    default:
      throw new Error(`Unexpected step status for send to document: ${step.statusCode}`);
  }
}

/**
 * To get the selected UDF charts, we need to use the step inputReferences to know which charts were selected
 */
function getSelectedUDFCharts(
  analysis: AnalysisGraphQL,
  step: GetAnalysisPipelineStep,
  parentStep: GetAnalysisPipelineStep,
  chartInputReferences: Array<{inputDataIndex: number}>
): Array<GetAnalysisChart> {
  const chartParentSteps = getParentSteps(analysis, step).filter((step) =>
    [
      PipelineStepType.USER_DEFINED_FUNCTION,
      PipelineStepType.CHART_VIEW,
      PipelineStepType.REGRESSION,
    ].includes(step.definition.stepType)
  );

  const allChartsFlattened: Array<{
    chart: GetAnalysisChart;
    parentStep: GetAnalysisPipelineStep;
  }> = [];
  chartParentSteps.forEach((chartStep) => {
    if (chartStep.definition.stepType === PipelineStepType.USER_DEFINED_FUNCTION) {
      const stepCharts = findAndAssertStepDataCharts(
        chartStep,
        MULTIPLE_CHARTS_OUTPUT_STEP_DATA_NAME,
        false,
        // UDFs may not always output N_CHARTS > 1, so we should not enforce charts' existence
        true
      );
      stepCharts.forEach((chart) => allChartsFlattened.push({chart, parentStep: chartStep}));
    } else {
      const chart = findAndAssertSingleStepChart(chartStep, SINGLE_CHART_OUTPUT_STEP_DATA_NAME, false);
      allChartsFlattened.push({chart, parentStep: chartStep});
    }
  });

  return chartInputReferences
    .map((ref) => allChartsFlattened[ref.inputDataIndex])
    .filter((item) => item && item.parentStep === parentStep)
    .map((item) => item.chart);
}

function getCatalogExportFileLabel(parentStep: GetAnalysisPipelineStep): string {
  const outputStepData = parentStep.outputStepDatas.find(
    (stepData) => stepData.name === SINGLE_FILE_OUTPUT_STEP_DATA_NAME
  );
  const scratchFile = outputStepData?.scratchFiles.find((file) => file.__typename === 'ScratchFileNode');
  if (scratchFile == null || scratchFile.__typename !== 'ScratchFileNode') {
    return 'File';
  }
  return scratchFile.filename;
}

interface SelectedScratchPlateGroup {
  inputDataIndex: number;
  parentStepId: string;
  title: string;
  plates: Array<ScratchPlateOutputNode>;
}

export type SendToDocumentViewProps = MainViewComponentProps<AnalysisMainViewItemType.SEND_TO_DOCUMENT>;

export default function SendToDocumentView({
  analysis,
  step,
  analysisClientStateId,
  clientStateId,
}: SendToDocumentViewProps): JSX.Element {
  assert(isAnalysisGraphQL(analysis), 'SendToDocumentView only supports analyses.');
  useLogAnalysisProfilerTimingFinished(
    step.statusCode === PipelineStepStatusCode.WAITING || step.statusCode === PipelineStepStatusCode.RUNNING,
    [SEND_TO_DOCUMENT_FINISHED_EVENT]
  );
  const stepConfig = getSendToDocumentStepConfig(step);
  const sectionLayout = stepConfig.sectionLayout ?? [];
  const parentStepsForSendToDocument = getParentSteps(analysis, step);
  const inputReferences = sectionLayout.flatMap((section) => section.inputReferences);
  const plateInputReferences = inputReferences.filter(
    (ref) => ref.inputDataName === SendToDocumentStepDataName.INPUT_PLATES
  );
  const chartInputReferences = inputReferences.filter(
    (ref) => ref.inputDataName === SendToDocumentStepDataName.INPUT_CHARTS
  );

  // Plate edges carry no slot index, so the Nth INPUT_PLATES edge is the group at `inputDataIndex` N.
  const domainStep = getNullableStepFromAnalysis(analysis, step.id);
  const plateEdgesInOrder = (domainStep?.incomingEdges ?? []).filter(
    (edge) => edge.toStepData.name === SendToDocumentStepDataName.INPUT_PLATES
  );
  const plateEdgesByInputDataIndex = new Map<number, Array<PipelineEdge>>(
    plateEdgesInOrder.map((edge, inputDataIndex) => [inputDataIndex, [edge]])
  );
  const inputEdgesByReference = new Map([
    ...(domainStep?.incomingEdges ?? [])
      .filter(
        (edge) =>
          edge.toStepData.name !== SendToDocumentStepDataName.INPUT_PLATES && edge.toStepDataIndex != null
      )
      .map((edge): [string, PipelineEdge] => [`${edge.toStepData.name}:${edge.toStepDataIndex}`, edge]),
    ...plateEdgesInOrder.map((edge, inputDataIndex): [string, PipelineEdge] => [
      `${SendToDocumentStepDataName.INPUT_PLATES}:${inputDataIndex}`,
      edge,
    ]),
  ]);
  const plateParentStepIds = new Set(plateEdgesInOrder.map((edge) => edge.fromStep.id));

  const stepTypeInfos = readStepTypeInfosFromCache();

  // A send-all edge (null `fromStepDataIndex`) collapses to a single plate in the domain mapper, so
  // resolve the whole source slot here rather than trusting the collapsed edge's lone plate.
  function resolveScratchPlatesOnSlot(
    fromStepId: string,
    fromStepDataName: string
  ): Array<ScratchPlateOutputNode> {
    const stepData = getNullableStepFromAnalysis(analysis, fromStepId)?.outputStepDatas.find(
      (data) => data.name === fromStepDataName
    );
    return (stepData?.rawDatas ?? []).filter((rawData) =>
      pipelineStepRawDataIsKind(rawData, PipelineStepDataKind.PLATE)
    );
  }

  const selectedScratchPlateGroups: Array<SelectedScratchPlateGroup> = plateInputReferences.flatMap(
    (inputReference) => {
      const edges = plateEdgesByInputDataIndex.get(inputReference.inputDataIndex) ?? [];
      if (edges.length === 0) {
        assertAndContinue(false, 'Expected an incoming edge for selected plate input', {
          inputDataIndex: inputReference.inputDataIndex,
        });
        return [];
      }
      const firstEdge = edges[0];
      const plates = resolveScratchPlatesOnSlot(firstEdge.fromStep.id, firstEdge.fromStepData.name);
      if (plates.length === 0) {
        assertAndContinue(false, 'Expected selected plate input to resolve to scratch plates', {
          fromStepDataName: firstEdge.fromStepData.name,
          fromStepId: firstEdge.fromStep.id,
        });
        return [];
      }
      const sourceStepType = getNullableStepFromAnalysis(analysis, firstEdge.fromStep.id)?.stepType;
      return [
        {
          inputDataIndex: inputReference.inputDataIndex,
          parentStepId: firstEdge.fromStep.id,
          title: getPlateEdgeLabel(
            sourceStepType != null ? getUserFacingStepTypeName(sourceStepType, stepTypeInfos) : '',
            firstEdge.fromStepData.name,
            plates.length
          ),
          plates,
        },
      ];
    }
  );
  const parentStepById = new Map(
    parentStepsForSendToDocument.map((parentStep) => [parentStep.id, parentStep])
  );
  const parentStepsInLayoutOrder = inputReferences.flatMap((inputReference) => {
    const edge = inputEdgesByReference.get(
      `${inputReference.inputDataName}:${inputReference.inputDataIndex}`
    );
    const parentStep = edge == null ? null : parentStepById.get(edge.fromStep.id);
    return parentStep == null ? [] : [parentStep];
  });
  const parentStepsForRendering =
    inputReferences.length > 0 && parentStepsInLayoutOrder.length === inputReferences.length
      ? parentStepsInLayoutOrder
      : parentStepsForSendToDocument;

  const allParentSteps = parentStepsForRendering.filter(
    (step) =>
      [
        PipelineStepType.SAVE_TO_RESULTS,
        PipelineStepType.EXPORT_DATA_CATALOG_DATASET,
        PipelineStepType.EXPORT_DATA_CATALOG_FILE,
        PipelineStepType.USER_DEFINED_FUNCTION,
        PipelineStepType.CHART_VIEW,
        PipelineStepType.REGRESSION,
      ].includes(step.definition.stepType) || plateParentStepIds.has(step.id)
  );

  // Deduplicate UDF and plate parent steps (each backs multiple outputs rendered in one pass); keep
  // all instances of other step types.
  const parentSteps = allParentSteps.filter((step, index, array) => {
    if (
      step.definition.stepType === PipelineStepType.USER_DEFINED_FUNCTION ||
      plateParentStepIds.has(step.id)
    ) {
      return array.findIndex((s) => s.id === step.id) === index;
    }
    return true;
  });

  return (
    <div
      className={css(styles.container)}
      {...getTestProps({component: 'SendToDocumentView', element: 'root'})}
    >
      <div className={css(styles.headerWrapper)}>
        <SendToDocumentHeader analysis={analysis} analysisClientStateId={analysisClientStateId} step={step} />
      </div>
      <div className={css(styles.outputsContainer)}>
        {/* oxlint-disable-next-line array-callback-return */}
        {parentSteps.map((parentStep, i) => {
          switch (parentStep.statusCode) {
            case PipelineStepStatusCode.SUCCEEDED:
            case PipelineStepStatusCode.SUCCEEDED_WITH_ERRORS: {
              if (parentStep.definition.stepType === PipelineStepType.SAVE_TO_RESULTS) {
                return (
                  <SavedResultsQuery
                    dataFrameId={findAndAssertSingleStepDataFrameId(
                      parentStep,
                      SaveToResultsStepDataName.SAVED_RESULTS
                    )}
                    resultSchemaId={
                      getPipelineStepConfig(parentStep, PipelineStepType.SAVE_TO_RESULTS, 2).schemaId
                    }
                    rowLimit={SEND_TO_DOCUMENT_VIEW_RESULT_ROW_LIMIT}
                    key={`saved-results-${parentStep.id}`}
                  >
                    {(totalNumSavedResults, results, resultSchema) => (
                      <>
                        {totalNumSavedResults > results.length && (
                          <div className={css(styles.resultLimitInfoBanner)}>
                            <InlineBanner
                              bannerType={InlineBannerType.WARNING}
                              text={`Showing the first ${SEND_TO_DOCUMENT_VIEW_RESULT_ROW_LIMIT} Results of ${totalNumSavedResults}.`}
                            />
                          </div>
                        )}
                        <div
                          className={classNames(
                            'mediocre',
                            'react-tab-container--mediocre-tableWrapper',
                            'react-tab-container--mediocre-tableWrapper--noPaddingTop'
                          )}
                        >
                          <div className="mediocre-item is-table">
                            <AssayResultsTable
                              additionalColumns={[]}
                              attachedForReview={false}
                              forceRowCount={totalNumSavedResults}
                              results={results}
                              schema={resultSchema}
                              canRename={false}
                              updateDisplayConfigHandler={() => {}}
                              {...standaloneProps}
                              fileId=""
                              cid={clientStateId}
                              locked={true}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </SavedResultsQuery>
                );
              } else if (parentStep.definition.stepType === PipelineStepType.EXPORT_DATA_CATALOG_FILE) {
                return (
                  <Chip
                    key={`file-chip-${parentStep.id}`}
                    icon="file"
                    isReadonly={true}
                    size={ChipSize.SMALL}
                    text={getCatalogExportFileLabel(parentStep)}
                  />
                );
              } else if (parentStep.definition.stepType === PipelineStepType.EXPORT_DATA_CATALOG_DATASET) {
                return (
                  <PersistedDataFrameTableWrapper
                    actionOrigin={ActionOrigin.ANALYSIS_TOOL}
                    clientStateId={clientStateId}
                    dataFrameId={findAndAssertSingleStepDataFrameId(
                      parentStep,
                      SINGLE_DATASET_OUTPUT_STEP_DATA_NAME
                    )}
                    key={`dataset-table-${i}`}
                  />
                );
              } else if (parentStep.definition.stepType === PipelineStepType.USER_DEFINED_FUNCTION) {
                const selectedChartsFromThisStep = getSelectedUDFCharts(
                  analysis,
                  step,
                  parentStep,
                  chartInputReferences
                );

                return selectedChartsFromThisStep.map((chart, chartIndex) => (
                  <Pane title={chart.name} key={`chart-${i}-${chartIndex}`}>
                    <div className={css(styles.chartPreview)}>
                      <QueryingChartContent chartId={chart.id} />
                    </div>
                  </Pane>
                ));
              } else if (plateParentStepIds.has(parentStep.id)) {
                return selectedScratchPlateGroups
                  .filter((group) => group.parentStepId === parentStep.id)
                  .map((group) => (
                    <ScratchPlateOutputPreview
                      key={`plate-group-${parentStep.id}-${group.inputDataIndex}`}
                      scratchPlates={group.plates}
                      title={group.title}
                    />
                  ));
              } else {
                assert(
                  [PipelineStepType.CHART_VIEW, PipelineStepType.REGRESSION].includes(
                    parentStep.definition.stepType
                  )
                );
                const chart = findAndAssertSingleStepChart(
                  parentStep,
                  SINGLE_CHART_OUTPUT_STEP_DATA_NAME,
                  false
                );
                return (
                  <Pane title={chart.name} key={`chart-${i}`}>
                    <div className={css(styles.chartPreview)}>
                      <QueryingChartContent chartId={chart.id} />
                    </div>
                  </Pane>
                );
              }
            }
            case PipelineStepStatusCode.FAILED:
              assert(
                parentStep.statusMessage != null,
                'Expected statusMessage to be defined for FAILED step.'
              );
              if (plateParentStepIds.has(parentStep.id)) {
                return (
                  <InlineBanner
                    bannerType={InlineBannerType.ERROR}
                    key={`plate-output-error-${parentStep.id}`}
                    supportingInfo={parentStep.statusMessage}
                    text="Error preparing plate output"
                  />
                );
              }
              if (parentStep.definition.stepType === PipelineStepType.EXPORT_DATA_CATALOG_FILE) {
                return (
                  <InlineBanner
                    bannerType={InlineBannerType.ERROR}
                    key={`file-output-error-${parentStep.id}`}
                    supportingInfo={parentStep.statusMessage}
                    text="Error preparing file output"
                  />
                );
              }
              return (
                <DeleteFailedSaveToResultsStepBanner
                  deleteStep={
                    analysis.permissions.writable
                      ? () =>
                          deleteAnalysisSteps(
                            analysis,
                            {
                              stepIds: [parentStep.id],
                              behavior: DeletePipelineStepsBehavior.DELETE_ALL_CHILDREN,
                            },
                            analysisClientStateId
                          )
                      : undefined
                  }
                  supportingInfo={parentStep.statusMessage}
                  key={`saved-results-${parentStep.id}`}
                />
              );
            case PipelineStepStatusCode.RUNNING:
            case PipelineStepStatusCode.WAITING:
            case PipelineStepStatusCode.BLOCKED:
              // TODO: Show in-progress save to results steps
              return <></>;
            case PipelineStepStatusCode.HALTED:
              return null;
            default:
              throw new Error(`Unexpected parent output step status: ${parentStep.statusCode}`);
          }
        })}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  chartPreview: {
    height: '400px',
  },
  openDocumentButton: {
    display: 'flex',
    flexDirection: 'row-reverse',
    alignItems: 'center',
  },
  headerWrapper: {
    marginBottom: '12px',
  },
  resultLimitInfoBanner: {
    marginBottom: '12px',
  },
  headerLineWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  helpText: {...FONT_PROXIMA_11, color: Color.GRAY_600},
  documentName: FONT_PROXIMA_18,
  documentIcon: {
    color: Color.GRAY_600,
    marginRight: '10px',
  },
  hourglassIcon: {
    color: Color.BLUE_450,
  },
  container: {
    margin: ANALYSIS_TOOL_SPACING,
  },
  outputsContainer: {
    // Grid (not flex column) so each Pane sizes to its content instead of compressing under
    // `u-flex-full-height`; the bounded container is the scroll region.
    display: 'grid',
    gridAutoRows: 'max-content',
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    rowGap: '12px',
  },
});
